import React, { useCallback, useState } from 'react';

import { Button, SpinnerOverlay } from '@components/common';
import FlatTreeXML, { IFlatTreeProps } from '@components/common/FlatXMLTree/FlatTreeXML';

import useMutateChangeMonitoringEdit from '@hooks/networkElementConfigurations/useMutateEditChangeMonitoring';
import useMutateNetworkElementValidationRules
	from '@hooks/networkElementConfigurations/useMutateNetworkElementValidationRules';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import {
	filterChangedNodes,
	getXMLString,
	prepareDataForSendingToCandidate,
} from '@components/routes/AdminPanel/ManageNetworkElementConfigurations/utils';

import { IValidationRuleDTO } from '@interfaces/networkElementConfigurations/dto/networkElementConfigurationsDTO';
import { TMapTree } from '@components/common/FlatXMLTree/interface';

import { ESnackbarTypes } from '@enums/common';

import { responseMessages } from '@constants/responseMessages';

import styles from './EditChanges.module.scss';

export interface IEditChangesModalProps extends IFlatTreeProps {
	onClose: () => void;
	closeModal: () => void;
	changedIds: string[];
	updateId: string;
	folderId: string;
}

export default function EditChangesModal({
	data: treeData,
	isReadOnly,
	withSearch,
	setChangedNodeIds,
	setData,
	onClose,
	closeModal,
	changedIds,
	updateId,
	folderId,
}: IEditChangesModalProps) {
	const dispatch = useAppDispatch();

	const [isCandidateTab, setIsCandidateTab] = useState<boolean>(false);
	const [candidateData, setCandidateData] = useState<TMapTree>(new Map());

	const {
		getValidationRules,
	} = useMutateNetworkElementValidationRules();

	const getValidationUpdateString = useCallback(async (id: string): Promise<IValidationRuleDTO | undefined> => {
		if (!id) return;

		const filteredData = filterChangedNodes({ data: treeData, changedIds: [id], isWithoutSiblings: true });
		const xmlString = getXMLString(filteredData);

		return {
			updateString: xmlString,
			validationRulesFolderId: folderId,
		};
	}, [
		folderId,
		treeData,
	]);

	const toggleIsCandidateTab = useCallback(() => {
		setIsCandidateTab((prev) => !prev);
	}, []);

	const onSuccess = () => {
		dispatch(setSnackbar({
			type: ESnackbarTypes.success,
			message: responseMessages.candidateSuccess,
		}));
		closeModal();
	};

	const onError = () => {
		dispatch(setSnackbar({
			type: ESnackbarTypes.error,
			message: responseMessages.error,
		}));
	};

	const {
		isLoadingEditChangeMonitoring,
		editChange,
	} = useMutateChangeMonitoringEdit(onSuccess, onError);

	const toggleAndSetCandidateData = useCallback(() => {
		toggleIsCandidateTab();
		setCandidateData(filterChangedNodes({ data: treeData, changedIds }));
	}, [toggleIsCandidateTab, treeData, changedIds, setCandidateData]);

	const handleCandidate = useCallback(async () => {
		const updateStrings = await prepareDataForSendingToCandidate(
			candidateData,
			changedIds,
			true,
		);

		await editChange({
			updateId,
			updateString: updateStrings[0],
		});
	}, [candidateData, changedIds, editChange, updateId]);

	return (
		<div className={styles.wrapper}>
			{isLoadingEditChangeMonitoring && <SpinnerOverlay />}
			<div className={styles.treeEditHeight}>
				<FlatTreeXML
					data={treeData}
					getValidationRules={getValidationRules}
					getValidationUpdateString={getValidationUpdateString}
					isReadOnly={isReadOnly || isCandidateTab}
					setChangedNodeIds={setChangedNodeIds}
					setData={setData}
					withSearch={withSearch}
				/>
			</div>
			<div className={styles.buttonsWrapper}>
				<div className={styles.control}>
					<Button
						large
						onClick={onClose}
						text='Cancel'
					/>
					{isCandidateTab ? (
						<>
							<Button
								large
								onClick={toggleIsCandidateTab}
								text='Back to edit'
							/>
							<Button
								large
								onClick={handleCandidate}
								text='Submit update'
							/>
						</>
					) : (
						<Button
							disabled={!changedIds.length}
							large
							onClick={toggleAndSetCandidateData}
							text='Next'
						/>
					)}
				</div>
			</div>
		</div>
	);
}
